/**
 * The conversation screen — two plates, in the map's grungy style.
 *
 * One slice of the base-view stylesheet (see `styles/index.ts`). It used to be
 * a single flat Codex panel inside `root.css.ts`; on 2026-10-14 it took the
 * same plate recipe as the world map and the Survivor Record: rivets in the
 * corners, a hazard strip along the top, rust running down from it, speckle
 * and a scratch, a bevel and a dark inner edge.
 *
 * ─── Two plates, one overlapping the other ──────────────────────────────────
 * The NPC's 3D portrait is its own plate and the talk is another, exactly as
 * the map is a stage plus a floating options plate. The portrait plate sits
 * ABOVE the dialogue plate (z-index 3), rises a little over its top edge and
 * covers its left edge by OVERLAP px. The dialogue plate keeps a left gutter
 * wider than that overlap, so the portrait covers plate and never a word.
 * Under 720px they stack and the portrait overlaps the top edge instead.
 *
 * ─── Option icons ───────────────────────────────────────────────────────────
 * data-kind is set by DialogUI from the server's per-reply kind. The icon is
 * the action bar's own glyph (tag for trade, ruled page for mission).
 *
 * NO BACKTICKS anywhere in this file, comments included: the export below is
 * one template literal and a stray backtick terminates it. Write class names
 * in plain text.
 */
export default `
.bv-dialog {
  position: absolute; inset: auto 0 0 0; z-index: 10;
  display: flex; justify-content: center;
  padding: 0 16px max(22px, env(safe-area-inset-bottom));
}
.bv-dialog[hidden] { display: none; }

/* No frame of its own: the two plates are the frame. The top padding is the
   room the portrait rises into above the dialogue plate. */
.bv-dialog-panel {
  --dlg-overlap: 56px;
  display: flex; align-items: flex-end;
  width: min(860px, 100%); padding-top: 0;
}

/* ── Portrait plate ───────────────────────────────────────────────────── */
.bv-dialog-portrait {
  position: relative; z-index: 3; flex: 0 0 160px; height: 200px;
  align-self: flex-end; margin-right: calc(var(--dlg-overlap) * -1);
  border: 2px solid #120d08; border-radius: var(--radius-panel);
  background:
    radial-gradient(circle, #f0d9a8 0 1.2px, #7a5a28 2.4px, #1a1208 4px, transparent 4.6px) left 5px top 5px / 10px 10px no-repeat,
    radial-gradient(circle, #f0d9a8 0 1.2px, #7a5a28 2.4px, #1a1208 4px, transparent 4.6px) right 5px top 5px / 10px 10px no-repeat,
    radial-gradient(circle, #f0d9a8 0 1.2px, #7a5a28 2.4px, #1a1208 4px, transparent 4.6px) left 5px bottom 5px / 10px 10px no-repeat,
    radial-gradient(circle, #f0d9a8 0 1.2px, #7a5a28 2.4px, #1a1208 4px, transparent 4.6px) right 5px bottom 5px / 10px 10px no-repeat,
    linear-gradient(180deg, rgba(150,66,26,0.24), transparent) 20% 0 / 20px 100px no-repeat,
    linear-gradient(104deg, transparent 47%, rgba(255,235,200,0.05) 47.3%, transparent 48.1%) 0 0 / 360px 240px,
    radial-gradient(circle at 18% 24%, rgba(255,235,200,0.06) 0 1px, transparent 1.5px) 0 0 / 19px 15px,
    radial-gradient(circle at 70% 76%, rgba(0,0,0,0.45) 0 1px, transparent 1.6px) 0 0 / 27px 21px,
    linear-gradient(165deg, #3a2c1c, #1a1410);
  box-shadow: 0 0 0 1px #5a4326, 0 14px 30px rgba(0,0,0,0.7), 8px 6px 20px rgba(0,0,0,0.45),
              inset 0 2px 0 rgba(255,222,170,0.18), inset 0 -3px 0 rgba(0,0,0,0.55), inset 0 0 30px rgba(0,0,0,0.5);
}
/* The recessed window the NPC stands in. The rivets live in the plate's own
   margin, which is what the inset is for. */
.bv-dialog-frame {
  position: absolute; inset: 15px; overflow: hidden;
  border: 2px solid #0c0805; border-radius: var(--radius-well);
  background: radial-gradient(120% 90% at 50% 20%, #4a3320, var(--well));
  box-shadow: 0 0 0 1px #4a3920, inset 0 0 26px rgba(0,0,0,0.75);
}
.bv-dialog-frame img { width: 100%; height: 100%; object-fit: contain; opacity: 0.55; }
.bv-npc-viewer { position: absolute; inset: 0; }
.bv-npc-canvas { display: block; width: 100%; height: 100%; }

/* ── Dialogue plate ───────────────────────────────────────────────────── */
.bv-dialog-body {
  position: relative; z-index: 1; flex: 1 1 0; min-width: 0;
  padding: 22px 20px 18px calc(var(--dlg-overlap) + 24px);
  min-height: 232px;
  border: 2px solid #120d08; border-radius: var(--radius-panel);
  background:
    radial-gradient(circle, #f4e0b0 0 1.2px, #7a5a28 2.4px, #1a1208 4px, transparent 4.6px) right 6px top 12px / 10px 10px no-repeat,
    radial-gradient(circle, #f4e0b0 0 1.2px, #7a5a28 2.4px, #1a1208 4px, transparent 4.6px) right 6px bottom 6px / 10px 10px no-repeat,
    radial-gradient(circle, #f4e0b0 0 1.2px, #7a5a28 2.4px, #1a1208 4px, transparent 4.6px) left 6px bottom 6px / 10px 10px no-repeat,
    repeating-linear-gradient(135deg, #17100a 0 7px, #b8862e 7px 14px) 0 0 / 100% 4px no-repeat,
    linear-gradient(180deg, rgba(150,66,26,0.26), transparent) 86% 4px / 24px 120px no-repeat,
    linear-gradient(180deg, rgba(130,56,22,0.2), transparent) 38% 4px / 16px 80px no-repeat,
    linear-gradient(104deg, transparent 52%, rgba(255,235,200,0.05) 52.3%, transparent 53%) 0 0 / 260px 180px,
    radial-gradient(circle at 20% 30%, rgba(255,235,200,0.06) 0 1px, transparent 1.5px) 0 0 / 17px 13px,
    radial-gradient(circle at 66% 72%, rgba(0,0,0,0.4) 0 1px, transparent 1.6px) 0 0 / 23px 17px,
    linear-gradient(168deg, #46361f, #1e1710);
  box-shadow: 0 0 0 1px #6b5230, 0 22px 44px rgba(0,0,0,0.75), 0 4px 0 rgba(0,0,0,0.5),
              inset 0 2px 0 rgba(255,222,170,0.25), inset 0 -3px 0 rgba(0,0,0,0.6), inset 0 0 30px rgba(0,0,0,0.4);
}
.bv-dialog-head {
  display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px 12px;
  margin-bottom: 10px; padding-bottom: 8px;
  border-bottom: 1px solid rgba(171,138,83,0.3);
  box-shadow: 0 1px 0 rgba(0,0,0,0.5);
}
.bv-dialog-name {
  font: 700 24px/1 var(--font-display); text-transform: uppercase;
  letter-spacing: 0.04em; color: var(--brass-hot); text-shadow: 0 2px 0 #000;
}
.bv-dialog-role {
  font: 12px var(--font-technical); color: var(--bone-dim);
  letter-spacing: 0.16em; text-transform: uppercase;
}
/* Prose, so the serif body face rather than the technical one. */
.bv-dialog-text {
  margin: 0 0 14px; color: var(--bone); font: 17px/1.5 var(--font-body);
  max-width: 60ch; text-shadow: 0 1px 2px rgba(0,0,0,0.6);
}

/* ── Options: scratched steel buttons ─────────────────────────────────── */
.bv-dialog-options { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 7px; }
.bv-dialog-opt {
  position: relative; overflow: hidden;
  display: grid; grid-template-columns: 28px 1fr auto; align-items: center; gap: 12px;
  width: 100%; min-height: 44px; padding: 7px 14px 7px 8px; text-align: left; cursor: pointer;
  font: 15px var(--font-technical); letter-spacing: 0.02em; color: var(--bone);
  border: 2px solid #0e0a06; border-radius: 3px;
  clip-path: polygon(0 3px, 3px 0, calc(100% - 5px) 0, 100% 4px, 100% calc(100% - 3px), calc(100% - 4px) 100%, 6px 100%, 0 calc(100% - 5px));
  background:
    linear-gradient(180deg, rgba(150,66,26,0.42), transparent 55%) 24% 0 / 14px 100% no-repeat,
    linear-gradient(180deg, rgba(120,48,20,0.36), transparent 70%) 71% 0 / 9px 100% no-repeat,
    linear-gradient(112deg, transparent 38%, rgba(255,235,200,0.1) 38.4%, transparent 39.2%),
    linear-gradient(74deg, transparent 63%, rgba(0,0,0,0.4) 63.3%, transparent 64.1%),
    radial-gradient(circle at 12% 30%, rgba(255,255,255,0.08) 0 1px, transparent 1.6px) 0 0 / 9px 7px,
    radial-gradient(circle at 66% 72%, rgba(0,0,0,0.45) 0 1.2px, transparent 1.8px) 0 0 / 12px 9px,
    linear-gradient(180deg, #4a3a25 0%, #2f2418 55%, #1b130b 100%);
  text-shadow: 0 1px 0 #000;
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.22), inset 0 -3px 0 rgba(0,0,0,0.5),
              inset 0 0 12px rgba(0,0,0,0.45), 0 3px 0 #0a0705, 0 5px 8px rgba(0,0,0,0.55);
  transition: transform 0.12s cubic-bezier(0.3, 1.5, 0.5, 1), filter 0.12s ease, color 0.12s ease, box-shadow 0.12s ease;
}
.bv-dialog-opt:hover, .bv-dialog-opt:focus-visible {
  color: var(--brass-hot); outline: none; transform: translateY(-2px); filter: brightness(1.18);
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.35), inset 0 -3px 0 rgba(0,0,0,0.5), inset 0 0 12px rgba(0,0,0,0.35),
              0 5px 0 #0a0705, 0 9px 14px rgba(0,0,0,0.55), 0 0 14px rgba(255,189,92,0.22);
}
.bv-dialog-opt:active { transform: translateY(2px); filter: brightness(0.9); box-shadow: inset 0 3px 8px rgba(0,0,0,0.7), 0 1px 0 #0a0705; }
.bv-dialog-opt:focus-visible { outline: 2px solid var(--brass-hot); outline-offset: 2px; }

/* The reply number, as a small stamped plate. */
.bv-dialog-num {
  display: flex; align-items: center; justify-content: center;
  width: 26px; height: 26px; box-sizing: border-box;
  font: 700 13px var(--font-display); color: var(--brass-bright);
  background: radial-gradient(90% 90% at 35% 25%, #3d2c18, #150f09);
  border: 1px solid #0a0705; border-radius: 3px;
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.2), inset 0 -2px 0 rgba(0,0,0,0.5);
}
.bv-dialog-label { min-width: 0; overflow-wrap: anywhere; }

/* The kind badge: a brass disc with the action bar glyph in it. */
.bv-dialog-kind {
  display: flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; box-sizing: border-box;
  color: #1a1208; border: 1px solid #3d2a0e; border-radius: 50%;
  background: radial-gradient(90% 90% at 35% 25%, #f0c46e, #b27d32 60%, #7a5320);
  box-shadow: inset 0 1px 0 rgba(255,246,222,0.5), 0 2px 3px rgba(0,0,0,0.6);
}
.bv-dialog-kind svg { width: 18px; height: 18px; display: block; }
/* Trade is a deal, brass all the way. A mission is a job on the table: the
   warn colour the game uses for "this is what you are about to do". */
.bv-dialog-opt[data-kind="trade"] { color: var(--brass-bright); }
.bv-dialog-opt[data-kind="mission"] .bv-dialog-kind {
  background: radial-gradient(90% 90% at 35% 25%, #f3d58a, #c98a2a 60%, #8a5a14);
}
.bv-dialog-opt[data-kind="mission"] { box-shadow: inset 4px 0 0 var(--warn), inset 0 1px 0 rgba(255,222,170,0.22), inset 0 -3px 0 rgba(0,0,0,0.5), inset 0 0 12px rgba(0,0,0,0.45), 0 3px 0 #0a0705, 0 5px 8px rgba(0,0,0,0.55); }

/* Narrow screens: stack. The portrait rises over the dialogue plate's top
   edge instead of its left, and the plate gives up its left gutter. */
@media (max-width: 720px) {
  /* Above the action bar dock (1002): on a phone the plate reaches the bottom
     and the dock used to sit on top of the last replies. */
  .bv-dialog { padding-inline: 10px; z-index: 1100; }
  /* Still a row: the portrait stays pinned to the bottom-left corner of the
     panel whatever the text or option count does to the plate's height. */
  .bv-dialog-panel { --dlg-overlap: 30px; flex-direction: row; align-items: flex-end; padding-top: 0; }
  .bv-dialog-portrait { flex: 0 0 84px; width: 84px; height: 108px; margin: 0 calc(var(--dlg-overlap) * -1) 0 0; }
  .bv-dialog-frame { inset: 9px; }
  .bv-dialog-body { flex: 1 1 0; min-width: 0; min-height: 150px; padding: 16px 12px 14px calc(var(--dlg-overlap) + 12px); }
  .bv-dialog-text { font-size: 16px; }
}
@media (prefers-reduced-motion: reduce) {
  .bv-dialog-opt { transition: none; }
}

/* The brief's card: what the job needs and pays, under the giver's words. */
.bv-dialog-card {
  margin: 0 0 12px; padding: 10px 14px; border: 1px solid #4a3920; border-radius: var(--radius-well);
  background: linear-gradient(180deg, rgba(0,0,0,0.38), rgba(0,0,0,0.2)); box-shadow: inset 0 1px 3px rgba(0,0,0,0.6);
  font: 13px/1.5 var(--font-technical); color: var(--bone);
}
.bv-dialog-card[hidden] { display: none; }
.bv-dialog-card-title { font: 700 11px var(--font-technical); letter-spacing: 0.12em; text-transform: uppercase; color: var(--warn); margin-bottom: 4px; }
.bv-dialog-card ul { margin: 0; padding-left: 18px; }
.bv-dialog-card li::marker { color: var(--warn); }
.bv-dialog-card-reward { margin-top: 6px; padding-top: 6px; border-top: 1px dashed #4a3920; color: var(--bone-dim); }
.bv-dialog-card-reward span { color: var(--brass-bright); margin-right: 8px; font-weight: 700; text-transform: uppercase; font-size: 10px; letter-spacing: 0.1em; }

/* Accept: the commit button. Moss, like Extract. */
.bv-dialog-opt[data-kind="accept"] { color: var(--bone); background: linear-gradient(180deg, #2c3a1e, #1a2410); border-color: var(--moss); }
.bv-dialog-opt[data-kind="accept"]:hover, .bv-dialog-opt[data-kind="accept"]:focus-visible { border-color: var(--moss); filter: brightness(1.2); }
.bv-dialog-opt[data-kind="accept"] .bv-dialog-kind { background: radial-gradient(90% 90% at 35% 25%, #b9d98a, #6f8f3f 60%, #46602a); }

/* Locked: shown, dim, with what is missing. */
.bv-dialog-opt:disabled, .bv-dialog-opt[data-locked] { opacity: 0.55; cursor: not-allowed; filter: grayscale(0.6); }
.bv-dialog-opt[data-locked]:hover { transform: none; border-color: inherit; filter: grayscale(0.6); }
.bv-dialog-lock-note { margin-left: auto; padding-left: 10px; font-size: 11px; color: var(--rust); white-space: nowrap; }
.bv-dialog-kind-lock { background: #2a2118; color: var(--bone-faint); box-shadow: none; }
`;
